/**
 * Utility functions for the application
 */

import React from 'react';
import { parseISO, format } from 'date-fns';
import * as dateFnsLocales from 'date-fns/locale';
import { v4 as uuidv4 } from 'uuid';
import apiCall from './apiCall';
import { APP_VERSION, LANGUAGE_MAP, DATE_FNS_LOCALE_MAP } from './constants.js';
import i18n from '../i18n/config';
import DOMPurify from 'dompurify';

/**
 * Get the day number for the current date within a plan's date range
 * @param {string|Date} startDate - Plan start date
 * @param {string|Date} endDate - Plan end date
 * @returns {number} Day number (0-based) if current date is in range, -1 otherwise
 */
export function getCurrentDayInPlan(startDate, endDate) {
  if (!startDate || !endDate) {
    return -1;
  }
  
  const currentDate = new Date();
  currentDate.setHours(0, 0, 0, 0);
  
  const start = new Date(startDate);
  start.setHours(0, 0, 0, 0);
  
  const end = new Date(endDate);
  end.setHours(0, 0, 0, 0);
  
  // Check if current date is within plan date range
  if (currentDate < start || currentDate > end) {
    return -1;
  }
  
  // Calculate day number: difference in days from start date
  const diffTime = currentDate - start;
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
  
  return diffDays;
}

/**
 * Get language name from language code
 * @param {string} languageCode - Language code (e.g., 'en', 'fr')
 * @returns {string} Language name (e.g., 'English', 'French') or 'English' as fallback
 */
export function getLanguageName(languageCode = 'en') {
  return LANGUAGE_MAP[languageCode] || 'English';
}

/**
 * Parse a JSON setting value
 * @param {string} value - Setting value string (expected to be JSON)
 * @returns {any} Parsed JSON value or original string if parsing fails
 */
export function parseSettingValue(value) {
  if (!value) return null;
  
  try {
    return JSON.parse(value);
  } catch (error) {
    console.error(`Error parsing setting value:`, error);
    return value; // Return raw value if parsing fails
  }
}

/**
 * Recursively clean "In English : " prefix from fields ending with "InEnglish"
 * Only removes the prefix if it appears at the start of the string value
 * @param {any} obj - The object to clean
 * @returns {any} - The cleaned object
 */
function cleanInEnglishPrefix(obj) {
  if (obj === null || obj === undefined) {
    return obj;
  }
  
  // If it's an array, recursively clean each element
  if (Array.isArray(obj)) {
    return obj.map(item => cleanInEnglishPrefix(item));
  }
  
  // If it's an object, recursively clean each property
  if (typeof obj === 'object') {
    const cleaned = {};
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        const value = obj[key];
        
        // Check if the key ends with "InEnglish" (case-insensitive)
        if (typeof key === 'string' && /InEnglish$/i.test(key)) {
          // This is an "InEnglish" field - clean the value if it's a string
          if (typeof value === 'string') {
            // Remove "In English : " or "In English:" prefix (case-insensitive, with optional colon and spaces)
            cleaned[key] = value.replace(/^In\s+English\s*:\s*/i, '').trim() || value;
          } else {
            // Not a string, keep as-is but still recurse if it's an object/array
            cleaned[key] = cleanInEnglishPrefix(value);
          }
        } else {
          // Not an "InEnglish" field - recurse but don't clean
          cleaned[key] = cleanInEnglishPrefix(value);
        }
      }
    }
    return cleaned;
  }
  
  // Primitive value - return as-is
  return obj;
}

/**
 * Utility function to safely parse JSON without throwing exceptions
 * @param {string} jsonString - The JSON string to parse
 * @returns {Object} Object with jsonData (parsed data or empty object) and result (true if successful, false if failed)
 */
export function tryParseJson(jsonString) {
  try {
    // Clean the response before parsing (remove markdown code blocks)
    let cleanedString = jsonString.trim();
    cleanedString = cleanedString.replace(/^```json\s*/, '').replace(/^```\s*/, '').replace(/\s*```$/, '');
    
    const jsonData = JSON.parse(cleanedString);
    
    // Clean "In English : " prefix from fields ending with "InEnglish"
    const cleanedData = cleanInEnglishPrefix(jsonData);
    
    return {
      jsonData: cleanedData,
      result: true
    };
  } catch (error) {
    console.error('JSON parsing error:', error);
    return {
      jsonData: {},
      result: false
    };
  }
}

/**
 * Function to try to extract JSON from text using AI
 * @param {string} prompt - The text containing potential JSON
 * @param {string} context - Context to provide to the AI
 * @param {string} systemContent - System content for the AI request
 * @param {string} feature - Feature name for token tracking (optional)
 * @returns {Object|null} Extracted JSON object or empty object if extraction failed
 */
export async function tryGetJson(prompt, context, systemContent, feature = 'unknown') {
  try {
    const response = await apiCall("/api/rawAi", { 
      prompt,
      context,
      systemContent,
      feature
    }, 'POST');
    
    if (response.success) {
      try {
        // Try to parse the JSON
        const parsedData = JSON.parse(response.rawContent);
        return parsedData;
      } catch (jsonError) {
        console.error("JSON parsing error:", jsonError);
        return {};
      }
    } else {
      console.error('API call failed:', response.message || 'Unknown error');
      return {};
    }
  } catch (err) {
    console.error('Error in API call:', err);
    return {};
  }
}

/**
 * Calculate duration between two dates safely
 * @param {string} startDate - Start date string in ISO format
 * @param {string} endDate - End date string in ISO format
 * @returns {string} Duration string (e.g. "5 days") or error message
 */
export function calculateDuration(startDate, endDate) {

  const value = calculateDurationInDays(startDate, endDate);
  if (value === -1) return 'Error calculating duration';  
}

/**
 * Calculate duration between two dates safely
 * @param {string} startDate - Start date string in ISO format
 * @param {string} endDate - End date string in ISO format
 * @returns {number} Duration in days or -1 if error
 */
export function calculateDurationInDays(startDate, endDate) {
  if (!startDate || !endDate) return -1;
  try {
    const start = new Date(startDate);
    const end = new Date(endDate);
    return Math.ceil((end - start) / (1000 * 60 * 60 * 24)) ;
  } catch (e) {
    return -1;
  }
}

/**
 * Helper function to copy text to clipboard
 * @param {string} text - The text to copy to clipboard
 */
export function copyToClipboard(text) {
  navigator.clipboard.writeText(text);
}

/**
 * Get supported currencies from settings data
 * @param {Object} settingsData - The settings data from API
 * @returns {Array} Array of supported currencies or empty array if not found
 */
export function getSupportedCurrencies(settingsData) {
  return settingsData?.currencies || [];
}

/**
 * Get default currency from settings data
 * @param {Object} settingsData - The settings data from API
 * @returns {string} Default currency code or 'USD' if not found
 */
export function getDefaultCurrency(settingsData) {
  return settingsData?.defaultCurrency || 'USD';
}

/**
 * Normalize a currency value by removing currency symbols and commas
 * @param {string|number} value - The currency value to normalize
 * @returns {number} - The normalized numeric value
 */
export function normalizeCurrencyValue(value) {
  if (typeof value === 'number') return value;
  if (!value) return 0;
  
  // Remove currency symbols, commas, and other non-numeric characters
  // except for decimal point
  const numericString = value.toString()
    .replace(/[$€£¥₹₪]/g, '') // Remove common currency symbols
    .replace(/,/g, '')        // Remove commas
    .replace(/[^\d.-]/g, '')  // Remove any other non-numeric chars
    .trim();
    
  const parsedValue = parseFloat(numericString);
  return isNaN(parsedValue) ? 0 : parsedValue;
}

/**
 * Extracts just the time (HH:MM) from an ISO date string
 * @param {string} dateString - ISO date string to extract time from
 * @returns {string} Extracted time or 'N/A' if invalid
 * @private - Internal helper function
 */
function extractTimeFromISOString(dateString) {
  if (!dateString || typeof dateString !== 'string') return 'N/A';
  
  try {
    // Extract just the hour and minute directly from the timestamp string
    const timePart = dateString.split('T')[1];
    if (!timePart) return 'N/A';
    
    const timeComponents = timePart.split(':');
    return `${timeComponents[0]}:${timeComponents[1]}`;
  } catch (error) {
    console.error('Time extraction error:', error, dateString);
    return 'N/A';
  }
}

/**
 * Extract hours from datetime string (pure string manipulation)
 * @param {string} dateTimeString - ISO datetime string
 * @returns {number} Hours (0-23) or 0 if invalid
 */
export function extractHours(dateTimeString) {
  if (!dateTimeString || typeof dateTimeString !== 'string') return 0;
  try {
    const timePart = dateTimeString.split('T')[1];
    if (!timePart) return 0;
    const timeComponents = timePart.split(':');
    if (timeComponents.length < 1) return 0;
    return parseInt(timeComponents[0], 10) || 0;
  } catch {
    return 0;
  }
}

/**
 * Extract minutes from datetime string (round to nearest 10, 20, 30, 40, 50)
 * @param {string} dateTimeString - ISO datetime string
 * @returns {number} Minutes (0, 10, 20, 30, 40, or 50) or 0 if invalid
 */
export function extractMinutes(dateTimeString) {
  if (!dateTimeString || typeof dateTimeString !== 'string') return 0;
  try {
    const timePart = dateTimeString.split('T')[1];
    if (!timePart) return 0;
    const timeComponents = timePart.split(':');
    if (timeComponents.length < 2) return 0;
    const minutes = parseInt(timeComponents[1], 10) || 0;
    // Round to nearest valid minute (10, 20, 30, 40, 50)
    const validMinutes = [0, 10, 20, 30, 40, 50];
    return validMinutes.reduce((prev, curr) => 
      Math.abs(curr - minutes) < Math.abs(prev - minutes) ? curr : prev
    );
  } catch {
    return 0;
  }
}

/**
 * Extracts just the date (YYYY-MM-DD) from an ISO date string
 * @param {string} dateString - ISO date string to extract date from
 * @returns {string} Extracted date or empty string if invalid
 * @private - Internal helper function
 */
function extractDateFromISOString(dateString) {
  if (!dateString || typeof dateString !== 'string') return '';
  
  try {
    // Extract just the date part directly from the timestamp string
    const datePart = dateString.split('T')[0];
    return datePart || '';
  } catch (error) {
    console.error('Date extraction error:', error, dateString);
    return '';
  }
}

/**
 * Safely format a date string
 * @param {string} dateString - ISO date string to format
 * @param {string} formatStr - Format string to use ('date', 'time', or date-fns format string)
 * @returns {string} Formatted date string or error message
 */
export function safeFormatDate(dateString, formatStr = 'PP') {
  try {
    if (!dateString) return '';
    
    // Handle time-only format
    if (formatStr === 'HH:mm' || formatStr === 'time') {
      return extractTimeFromISOString(dateString);
    }
    
    // Handle date-only format
    if (formatStr === 'yyyy-MM-dd' || formatStr === 'date') {
      return extractDateFromISOString(dateString);
    }
    
    // For all other formats, first extract the date using our utility
    const extractedDate = extractDateFromISOString(dateString);
    if (!extractedDate) return 'Invalid date';
    
    // Then format it using date-fns
    const date = parseISO(extractedDate);
    return format(date, formatStr);
  } catch (error) {
    console.error('Date formatting error:', error, dateString);
    return 'Invalid date';
  }
}

/**
 * Format datetime for datetime-local input (YYYY-MM-DDTHH:MM)
 * Handles all datetime formats robustly:
 * - "2025-12-12T18:00:00Z" 
 * - "2025-12-12T18:00:00.000Z"
 * - "2025-12-12T18:00:00"
 * - "2025-12-12T18:00"
 * @param {string} dateTimeString - The datetime string to format
 * @returns {string} Formatted datetime string (YYYY-MM-DDTHH:MM) or empty string
 */
export function formatForDateTimeLocal(dateTimeString) {
  if (!dateTimeString) return '';
  
  try {
    // Pure string manipulation - no Date objects
    // Remove timezone indicator (Z)
    let formatted = dateTimeString.replace('Z', '');
    
    // Remove milliseconds (.000, .123, etc.)
    formatted = formatted.replace(/\.\d+/, '');
    
    // Take first 16 characters (YYYY-MM-DDTHH:MM)
    formatted = formatted.substring(0, 16);
    
    // Validate format (basic check)
    if (formatted.length === 16 && formatted.includes('T')) {
      return formatted;
    }
    
    return '';
  } catch (error) {
    console.error('Error formatting datetime for input:', error, dateTimeString);
    return '';
  }
}

/**
 * Generates a UUID v4 (random)
 * @returns {string} A UUID string
 */
export function generateUUID() {
  return uuidv4();
}

/**
 * Format a date as dd-mm-yyyy
 * @param {Date} date - The date to format
 * @returns {string} Formatted date string
 */
export function formatDateToDayId(date) {
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
}

/**
 * Normalize date format to ensure consistency across the application.
 * This keeps the time (hours/minutes) from the original date, but uses 
 * the year, month, and day from the target date.
 * 
 * @param {string} dateString - The date string to normalize
 * @param {string} targetDateString - The target date to use for year/month/day
 * @returns {string} - Normalized ISO date string with consistent formatting
 */
// eslint-disable-next-line no-unused-vars
function normalizeDate(dateString, targetDateString) {
  if (!dateString || !targetDateString) return '';
  
  try {
    // Get the target day's date
    const targetDay = new Date(targetDateString);
    const year = targetDay.getFullYear();
    const month = targetDay.getMonth() + 1; // Months are 0-indexed in JS
    const day = targetDay.getDate();
    
    // Parse input date to extract time
    const inputDate = new Date(dateString);
    const hours = inputDate.getHours();
    const minutes = inputDate.getMinutes();
    
    // Format consistently with the target date's year/month/day and original time
    const normalized = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}T${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:00.000Z`;
    
    return normalized;
  } catch (err) {
    console.error('Error normalizing date:', err);
    return dateString;
  }
}

/**
 * Creates a Google Maps link from a location object or string
 * @param {Object|string} location - Location object with latitude/longitude or address string
 * @returns {string|null} Google Maps URL or null if no valid location data
 */
export function getGoogleMapsLink(location) {
  if (!location) return null;
  
  // Extract coordinates
  let lat, lng;
  if (typeof location === 'object') {
    lat = location.latitude;
    lng = location.longitude;
  }
  
  // Return a link if we have coordinates
  if (lat && lng) {
    return `https://www.google.com/maps?q=${lat},${lng}`;
  }
  
  // If no coordinates but we have an address, search for it
  const address = typeof location === 'object' ? location.address : location;
  if (address) {
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
  }
  
  return null;
}

/**
 * Build Google Maps search query string from an activity and role
 * Uses English names/addresses when available for better Google Maps compatibility
 * @param {Object} activity - Activity object (transportation, accommodation, or attraction)
 * @param {string} role - Role for transportation: 'from' | 'to' | 'any'. For other activities: 'any'
 * @returns {string|null} Google Maps search query string or null if no valid location
 */
export function buildGoogleMapsQuery(activity, role = 'any') {
  if (!activity) return null;
  
  // For transportation: use address + city/country
  if (activity.type === 'transportation') {
    const loc = role === 'from' ? activity.fromLocation : activity.toLocation;
    if (!loc) return null;
    
    let searchQuery = '';
    if (typeof loc === 'object') {
      // Prefer English address, fall back to regular address
      searchQuery = loc.addressInEnglish || loc.address || '';
      if (loc.city || loc.country) {
        const parts = [];
        if (searchQuery) parts.push(searchQuery);
        if (loc.city) parts.push(loc.city);
        if (loc.country) parts.push(loc.country);
        searchQuery = parts.join(', ');
      }
    } else {
      searchQuery = loc;
    }
    return searchQuery || null;
  }
  
  // For accommodation/attraction: use name + city/country
  // Prefer English name, fall back to regular name
  const activityName = activity.nameInEnglish || activity.name;
  if (!activityName) return null;
  
  const loc = activity.location;
  let searchQuery = activityName;
  if (loc && typeof loc === 'object' && (loc.city || loc.country)) {
    const parts = [];
    if (loc.city) parts.push(loc.city);
    if (loc.country) parts.push(loc.country);
    searchQuery += `, ${parts.join(', ')}`;
  }
  return searchQuery;
}

/**
 * Get the location role for the "from" side of a direction link
 * @param {Object} activity - Activity object
 * @returns {string} Role to use: 'to' for transportation (destination), 'any' for others
 */
function getFromRoleForActivity(activity) {
  if (!activity) return 'any';
  return activity.type === 'transportation' ? 'to' : 'any';
}

/**
 * Get Google Maps directions URL from current activity to next activity
 * @param {Object} fromActivity - Current activity
 * @param {Object} toActivity - Next activity
 * @param {string} toRole - Role for next activity ('from' | 'to' | 'any')
 * @returns {string|null} Google Maps directions URL or null if invalid
 */
export function getGoogleMapsDirectionsUrl(fromActivity, toActivity, toRole = 'any') {
  if (!fromActivity || !toActivity) return null;
  
  const fromRole = getFromRoleForActivity(fromActivity);
  const fromQuery = buildGoogleMapsQuery(fromActivity, fromRole);
  const toQuery = buildGoogleMapsQuery(toActivity, toRole);
  
  if (!fromQuery || !toQuery) return null;
  
  return `https://www.google.com/maps/dir/?api=1&origin=${encodeURIComponent(fromQuery)}&destination=${encodeURIComponent(toQuery)}`;
}

/**
 * Render address with English version in brackets (if different) and Google Maps link
 * @param {Object|string} location - Location object or string
 * @returns {JSX.Element|null} React element with address display or null if no location
 */
export function renderAddressWithEnglish(location) {
  if (!location) return null;
  
  const locationObj = typeof location === 'object' ? location : { address: location };
  const address = locationObj.address || '';
  const addressInEnglish = locationObj.addressInEnglish || '';
  const displayAddress = address;
  const englishAddress = addressInEnglish && address !== addressInEnglish ? ` (${addressInEnglish})` : '';
  
  // Create a location object with English address for Google Maps link
  const locationForMaps = typeof location === 'object' 
    ? { ...location, address: addressInEnglish || address }
    : (addressInEnglish || location);
  
  const mapsLink = getGoogleMapsLink(locationForMaps);
  
  if (mapsLink) {
    return (
      <a 
        href={mapsLink} 
        target="_blank" 
        rel="noopener noreferrer"
      >
        {displayAddress}{englishAddress}
      </a>
    );
  }
  
  return <span>{displayAddress}{englishAddress}</span>;
}

/**
 * Normalizes location data to ensure consistent structure
 * Handles both string locations and location objects
 * 
 * @param {string|Object} location - The location data to normalize
 * @returns {Object} - Normalized location object with address, latitude, longitude
 */
export function normalizeLocation(location) {
  if (typeof location === 'string') {
    return { address: location, latitude: '', longitude: '' };
  }
  return location || { address: '', latitude: '', longitude: '' };
}

/**
 * Fetches images for a list of attractions and attaches them to each attraction object.
 * @param {Array} attractions - Array of attraction objects (must have id and name)
 * @returns {Promise<Array>} - Array of attractions with images property attached
 */
export async function fetchAttractionImages(attractions) {
  if (!Array.isArray(attractions) || attractions.length === 0) return attractions;

  // Prepare payload for the API
  // Use English fields for image search (better results and consistent caching across languages)
  const attractionsForImageAPI = attractions.map(attraction => {
    const location = attraction.location || {};
    return {
      id: attraction.id || uuidv4(),
      name: attraction.nameInEnglish || attraction.name,
      nameInEnglish: attraction.nameInEnglish || null,
      location: {
        city: location.cityInEnglish || location.city || '',
        country: location.countryInEnglish || location.country || ''
      },
      terms: ''
    };
  });

  try {
    const response = await apiCall(
      '/api/model?action=207', // ApiActions.GET_ATTRACTION_IMAGES
      { attractions: attractionsForImageAPI },
      'POST'
    );

    if (response.success && response.results) {
      // Attach images to each attraction by id
      return attractions.map(attraction => {
        const attractionId = attraction.id || '';
        const imageResult = response.results[attractionId];
        if (imageResult && imageResult.images && imageResult.images.length > 0) {
          return {
            ...attraction,
            id: attraction.id || attractionId,
            images: imageResult.images
          };
        }
        return attraction;
      });
    }
  } catch (error) {
    console.error('Error fetching images for attractions:', error);
    // Return original attractions if error
  }
  return attractions;
}

/**
 * Add days to a date string (YYYY-MM-DD format)
 * Uses string-based manipulation only (no Date objects)
 * 
 * @param {string} dateStr - Date string in YYYY-MM-DD format
 * @param {number} days - Number of days to add (can be negative to subtract)
 * @returns {string} New date string in YYYY-MM-DD format
 */
export function addDaysToString(dateStr, days) {
  if (!dateStr || typeof dateStr !== 'string') return dateStr;
  
  const datePart = dateStr.split('T')[0];
  const [year, month, day] = datePart.split('-').map(Number);
  
  // Use Date.UTC for calculation but immediately convert back to string
  const date = new Date(Date.UTC(year, month - 1, day + days));
  return date.toISOString().split('T')[0];
}

/**
 * Calculate start and end datetime for a new activity on a given day
 * Uses string-based manipulation only (no Date objects)
 * 
 * @param {string} dayDate - Day's date string (ISO format: YYYY-MM-DD or YYYY-MM-DDTHH:mm:ss.sssZ)
 * @param {Array} existingActivities - Array of existing activities for the day (excluding accommodations)
 * @param {number} durationMinutes - Duration of the new activity in minutes (default: 120)
 * @returns {Object} Object with startDateTime and endDateTime (ISO format strings)
 */
export function calculateActivityDateTime(dayDate, existingActivities = [], durationMinutes = 120) {
  // Extract date part (YYYY-MM-DD)
  const dayPrefix = dayDate.split('T')[0];
  
  let startTime = '10:00:00.000Z'; // Default: 10:00 AM for empty days
  
  // If there are existing activities, find the latest valid same-day time and add 30 minutes
  if (existingActivities && existingActivities.length > 0) {
    const getMinutesForSameDay = (dateTime) => {
      if (!dateTime || typeof dateTime !== 'string') return null;
      const [datePart, timePartRaw] = dateTime.split('T');
      if (!datePart || datePart !== dayPrefix || !timePartRaw) return null;
      const timePart = timePartRaw.replace('Z', '');
      const [hStr, mStr] = timePart.split(':');
      const h = parseInt(hStr, 10);
      const m = parseInt(mStr, 10);
      if (Number.isNaN(h) || Number.isNaN(m)) return null;
      return h * 60 + m;
    };

    let latestMinutes = null;
    existingActivities.forEach((activity) => {
      const startMinutes = getMinutesForSameDay(activity.startDateTime);
      const endMinutes = getMinutesForSameDay(activity.endDateTime);

      // For malformed ranges (end before start on same day), use start as the latest point.
      let activityLatest = null;
      if (startMinutes != null && endMinutes != null) {
        activityLatest = endMinutes >= startMinutes ? endMinutes : startMinutes;
      } else if (endMinutes != null) {
        activityLatest = endMinutes;
      } else if (startMinutes != null) {
        activityLatest = startMinutes;
      }

      if (activityLatest != null && (latestMinutes == null || activityLatest > latestMinutes)) {
        latestMinutes = activityLatest;
      }
    });

    if (latestMinutes != null) {
      let nextStartMinutes = latestMinutes + 30;
      if (nextStartMinutes >= 22 * 60) {
        nextStartMinutes = 10 * 60;
      }
      const hours = Math.floor(nextStartMinutes / 60);
      const minutes = nextStartMinutes % 60;
      startTime = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:00.000Z`;
    }
  }
  
  // Calculate end time: start time + duration
  const startTimeParts = startTime.split(':');
  let endHours = parseInt(startTimeParts[0]) || 10;
  let endMinutes = parseInt(startTimeParts[1]) || 0;
  
  // Add duration
  endMinutes += durationMinutes;
  if (endMinutes >= 60) {
    endHours += Math.floor(endMinutes / 60);
    endMinutes = endMinutes % 60;
  }
  
  // Cap end time at 22:00 (10 PM)
  if (endHours >= 22) {
    endHours = 22;
    endMinutes = 0;
    // Adjust start time backwards if needed
    const totalMinutes = endHours * 60 + endMinutes;
    const adjustedStartMinutes = totalMinutes - durationMinutes;
    if (adjustedStartMinutes >= 0) {
      endHours = Math.floor(adjustedStartMinutes / 60);
      endMinutes = adjustedStartMinutes % 60;
      startTime = `${String(endHours).padStart(2, '0')}:${String(endMinutes).padStart(2, '0')}:00.000Z`;
      endHours = 22;
      endMinutes = 0;
    }
  }
  
  const endTime = `${String(endHours).padStart(2, '0')}:${String(endMinutes).padStart(2, '0')}:00.000Z`;
  
  return {
    startDateTime: `${dayPrefix}T${startTime}`,
    endDateTime: `${dayPrefix}T${endTime}`
  };
}

/**
 * Check if version v1 is bigger than v2
 * Converts versions to numbers by removing dots (e.g., "1.2.45" = 1245)
 * @param {string} v1 - First version
 * @param {string} v2 - Second version
 * @returns {boolean} True if v1 > v2
 */
export function isBiggerVersion(v1, v2) {
  const num1 = parseInt(v1.replace(/\./g, ''), 10);
  const num2 = parseInt(v2.replace(/\./g, ''), 10);
  return num1 > num2;
}

/**
 * Check if a new version is available by comparing server version in localStorage with client version
 * @returns {boolean} True if server version is bigger than client version (new version available), false otherwise
 */
export function isNewVersionAvailable() {
  const serverVersion = localStorage.getItem('serverVersion');
  
  if (!serverVersion) {
    return false; // No server version in localStorage, assume same version
  }
  
  // Only show refresh icon if server version is BIGGER than current client version
  // If client version is newer (or same), we're up to date
  return isBiggerVersion(serverVersion, APP_VERSION);
}

/**
 * Get tier hierarchy value for comparison (higher = better tier)
 * @param {string} tier - Access tier (free, pro, proplus, admin)
 * @returns {number} Hierarchy value (0 = free, 1 = pro, 2 = proplus, 3 = admin)
 */
export function getTierHierarchy(tier) {
  if (tier === 'admin') return 3;
  if (tier === 'proplus') return 2;
  if (tier === 'pro') return 1;
  return 0; // free or anything else
}

/**
 * Get the higher tier between two tiers
 * @param {string} tier1 - First tier to compare
 * @param {string} tier2 - Second tier to compare
 * @returns {string} The higher tier (or 'free' if both are invalid)
 */
export function getHigherTier(tier1, tier2) {
  const hierarchy1 = getTierHierarchy(tier1 || 'free');
  const hierarchy2 = getTierHierarchy(tier2 || 'free');
  return hierarchy1 >= hierarchy2 ? (tier1 || 'free') : (tier2 || 'free');
}

/**
 * Get badge color for itinerary option number
 * @param {number} optionNum - Option number (1, 2, or 3)
 * @returns {string} Bootstrap badge color variant
 */
export function getOptionBadgeColor(optionNum) {
  switch (optionNum) {
    case 1:
      return 'primary';
    case 2:
      return 'success';
    case 3:
      return 'warning';
    default:
      return 'info';
  }
}

/**
 * Get the appropriate date-fns locale based on language code
 * @param {string} languageCode - i18n language code (e.g., 'en', 'he', 'fr', 'es', 'ar')
 * @returns {object} date-fns locale object
 */
export function getDateFnsLocale(languageCode) {
  const lang = languageCode || 'en';
  const langCode = lang.split('-')[0]; // Get base language code (e.g., 'en' from 'en-US')
  
  const localeKey = DATE_FNS_LOCALE_MAP[langCode] || DATE_FNS_LOCALE_MAP[lang] || 'enUS';
  return dateFnsLocales[localeKey] || dateFnsLocales.enUS;
}

/**
 * Format date with locale support
 * Automatically uses the current i18n language
 * @param {string} dateString - ISO date string to format
 * @param {string} formatStr - date-fns format string (default: 'MMM d, yyyy')
 * @returns {string} Formatted date string
 */
export function formatDateWithLocale(dateString, formatStr = 'MMM d, yyyy') {
  if (!dateString) return '';
  try {
    const date = new Date(dateString);
    const languageCode = i18n.language || 'en';
    const locale = getDateFnsLocale(languageCode);
    return format(date, formatStr, { locale });
  } catch (error) {
    return safeFormatDate(dateString, formatStr);
  }
}

const HTML_SANITIZE_BASE_CONFIG = {
  ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'u', 's', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'ul', 'ol', 'li', 'a', 'img', 'table', 'thead', 'tbody', 'tr', 'th', 'td', 'blockquote', 'hr', 'div', 'span'],
  ALLOWED_ATTR: ['href', 'src', 'alt', 'title', 'class', 'style', 'width', 'height', 'target', 'rel'],
  ALLOWED_URI_REGEXP: /^(?:(?:(?:f|ht)tps?|mailto|tel|callto|sms|cid|xmpp|data):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i,
  ALLOW_DATA_ATTR: false
};

function runSanitizeHTML(html, extraConfig = {}) {
  if (!html) return '';

  // DOMPurify requires browser environment (window object)
  if (typeof window === 'undefined') {
    return '';
  }

  try {
    return DOMPurify.sanitize(html, {
      ...HTML_SANITIZE_BASE_CONFIG,
      ...extraConfig,
    });
  } catch (error) {
    console.error('Error sanitizing HTML:', error);
    return html;
  }
}

/**
 * Sanitize HTML content to prevent XSS attacks
 * Uses DOMPurify to remove dangerous elements while preserving safe formatting
 * @param {string} html - HTML string to sanitize
 * @returns {string} Sanitized HTML string
 */
export function sanitizeHTML(html) {
  return runSanitizeHTML(html);
}

/**
 * Sanitize Additional Info HTML, allowing scoped embeds (e.g. calendar widgets).
 * Still blocks script, iframe, event handlers, and other unsafe markup.
 * @param {string} html - HTML string to sanitize
 * @returns {string} Sanitized HTML string
 */
export function sanitizeInfoHTML(html) {
  return runSanitizeHTML(html, {
    ADD_TAGS: ['style'],
    ADD_ATTR: ['id'],
  });
} 